import React, { useCallback, useEffect, useState } from 'react';
import ApproachCard from '../../../components/approach/ApproachCard';
import Loading from '../../../components/Loading';
import MessageOdoFooterButton from '../../../components/patient/MessageOdoFooterButton';
import { useLayoutFooter, useStepperContext } from '../../../layouts/PatientCreationLayout';
import { useSubmissionForm } from '../../../providers/patient/PatientSubmissionContext';
import type { SubmissionFormData } from '../../../providers/patient/PatientSubmissionContext';
import { useToast } from '../../../providers/ToastProvider';
import { reportCaughtError } from '../../../services/errorReporting';
import { updatePatientCreation } from '../../../services/patient/creation';
import { TreatmentApproach } from './treatmentDetails';

const SubCaseType: React.FC = () => {
    const { formData, updateSubmissionForm } = useSubmissionForm();
    const { setFooterContent } = useLayoutFooter();
    const { goNext, goBack } = useStepperContext();
    const { showToast } = useToast();
    const [selectedApproach, setSelectedApproach] = useState<TreatmentApproach | undefined>(
        formData?.subCaseType
    );
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [isModified, setIsModified] = useState(false);

    const handleSelectApproach = useCallback(
        (approach: TreatmentApproach) => {
            setSelectedApproach(approach);
            updateSubmissionForm({ subCaseType: approach });
            setIsModified(true);
        },
        [updateSubmissionForm]
    );

    const handleNextClick = useCallback(async () => {
        setIsLoading(true);
        try {
            if (isModified && formData?.id && selectedApproach) {
                const result = await updatePatientCreation(formData.id, {
                    subCaseType: selectedApproach.monday_key
                });
                const updates: Partial<SubmissionFormData> = {};
                if (result.treatmentDuration !== undefined)
                    updates.treatmentDuration = result.treatmentDuration;
                if (Object.keys(updates).length > 0) {
                    updateSubmissionForm(updates);
                }
            }
            goNext();
        } catch (error) {
            showToast('Failed to save approach. Please try again.', 'error');
            reportCaughtError(error);
        } finally {
            setIsLoading(false);
        }
    }, [updateSubmissionForm, goNext, formData?.id, isModified, selectedApproach, showToast]);

    const handleBack = useCallback(() => {
        goBack();
    }, [goBack]);

    useEffect(() => {
        setFooterContent(
            <div className="w-full flex justify-end">
                <div className="w-full flex justify-start items-center gap-2">
                    {formData?.caseType && (
                        <div className="flex flex-col items-start gap-1">
                            <div className="flex items-center gap-2">
                                <p className="text-m font-bold text-gray-500">Selected :</p>
                                <p className="text-m text-gray-500">{formData.caseType.label}</p>
                                {selectedApproach && (
                                    <p className="text-m text-gray-500">
                                        {' '}
                                        - {selectedApproach.title}
                                    </p>
                                )}
                            </div>
                            {formData.caseType.disclaimer && (
                                <div className="w-full flex flex-col items-start">
                                    <p className="text-sm text-gray-500 italic">
                                        *{formData.caseType.disclaimer}
                                    </p>
                                </div>
                            )}
                        </div>
                    )}
                </div>
                <div className="shrink-0 flex items-center gap-3">
                    <MessageOdoFooterButton />
                    <button
                        disabled={isLoading || !selectedApproach}
                        onClick={handleNextClick}
                        className={`flex flex-row justify-center items-center px-4 py-3.5 gap-2 w-24 h-10 rounded-lg font-bold text-sm leading-4 text-center tracking-wider ${
                            isLoading || !selectedApproach
                                ? 'bg-gray-300 text-white cursor-not-allowed'
                                : 'bg-gray-800 text-white hover:bg-gray-700'
                        }`}
                    >
                        Continue
                    </button>
                </div>
            </div>
        );

        return () => {
            setFooterContent(null);
        };
    }, [setFooterContent, isLoading, handleNextClick, formData?.caseType, selectedApproach]);

    if (!formData?.caseType) {
        return (
            <div className="text-center">
                <p>No treatment case selected. Please go back.</p>
                <button
                    onClick={handleBack}
                    className="mt-4 px-4 py-2 bg-gray-800 text-white rounded-lg"
                >
                    Back
                </button>
            </div>
        );
    }

    const { approaches } = formData.caseType;

    return (
        <>
            {isLoading && <Loading text="Saving..." />}

            <div className="flex flex-row justify-center items-center">
                <h1 className="text-2xl font-bold text-gray-900 mb-8">
                    Select {formData.caseType.approachesTitle} approach
                </h1>
            </div>
            <div className="w-full flex flex-col items-center">
                <div className="w-full flex flex-col items-center space-y-4 max-w-5xl">
                    <div className="w-full flex flex-col items-start mb-4">
                        <p className="text-sm text-gray-500">
                            {formData.caseType.approaches_description}
                        </p>
                    </div>
                    {approaches.map((approach) => (
                        <ApproachCard
                            key={approach.key}
                            approach={approach}
                            isSelected={selectedApproach?.key === approach.key}
                            onSelect={() => handleSelectApproach(approach)}
                        />
                    ))}
                </div>
            </div>
        </>
    );
};

export default SubCaseType;
